import { z } from 'zod'
import { Model } from '../../_kernel/architecture'

export type Markets = (typeof ALLOWED_MARKETS)[number]

export const ALLOWED_MARKETS = [
  'ae',
  'at',
  'au',
  'be',
  'br',
  'ca',
  'ch',
  'de',
  'dk',
  'ee',
  'eg',
  'es',
  'fi',
  'fr',
  'gr',
  'hr',
  'ie',
  'it',
  'lt',
  'lv',
  'mx',
  'nl',
  'no',
  'pl',
  'pt',
  'ro',
  'sa',
  'se',
  'sk',
  'si',
  'tr',
  'uk',
  'us',
  'za',
  'nz',
  'hu',
  'cz',
  'co',
] as const

export const HOSTNAME_TO_MARKET = {
  'es.stayforlong.com': 'es',
  'ie.stayforlong.com': 'ie',

  'www.stayforlong.co.uk': 'uk',
  'www.stayforlong.de': 'de',
  'www.stayforlong.fr': 'fr',
} as { [key: string]: (typeof ALLOWED_MARKETS)[number] | undefined }

export const MarketValidation = z.object({
  value: z.enum(ALLOWED_MARKETS, { required_error: 'value is required' }),
})

export class Market extends Model {
  static create({ value }: z.infer<typeof MarketValidation>) {
    MarketValidation.parse({ value })
    return new Market(value)
  }

  static createOrEmpty({ value }: z.infer<typeof MarketValidation>) {
    try {
      return Market.create({ value })
    } catch {
      return Market.empty()
    }
  }

  static fromHostName({ hostname }: { hostname: string }) {
    const market = HOSTNAME_TO_MARKET[hostname]
    return market ? Market.create({ value: market }) : Market.empty()
  }

  static default() {
    return Market.create({ value: 'us' })
  }

  static fromJSON(json?: Partial<ReturnType<Market['toJSON']>>) {
    if (!json?.value) {
      return Market.empty()
    }
    return Market.create({ value: json.value })
  }

  static empty() {
    return new Market()
  }

  constructor(private readonly _value?: (typeof ALLOWED_MARKETS)[number]) {
    super()
  }

  get value() { return this._value } // prettier-ignore

  isImperialMetric() {
    return this.value === 'us' || this.value === 'ca'
  }

  isEmpty(): this is Market & { value: undefined } {
    return !this.value
  }

  isFilled(): this is Market & z.infer<typeof MarketValidation> {
    return !!this.value
  }

  is<T extends Markets>(value: T): this is Market & { value: T } {
    return this.isFilled() && this.value === value
  }

  isOneOf<T extends Markets>(market: readonly T[]): this is Market & { value: T } {
    return this.isFilled() && market.includes(this.value as T)
  }

  toJSON() {
    return { value: this.value ?? null }
  }
}
